import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { parseSearch, stringifySearch } from './searchCodec';
import { AuthOnly } from '../auth/AuthOnly';
import { AppGate } from './AppGate';
import { BootScreen } from './BootScreen';
import { ScreenRoute } from '../pages/ScreenRoute';
import { NotFoundPage } from '../pages/NotFoundPage';
import { reportDefinition } from '../reports/types';

const LoginPage = lazyRouteComponent(() => import('../auth/LoginPage'), 'LoginPage');
const SetupPage = lazyRouteComponent(() => import('../auth/SetupPage'), 'SetupPage');
const CodePage = lazyRouteComponent(() => import('../auth/CodePage'), 'CodePage');
const ObjectPage = lazyRouteComponent(() => import('../pages/ObjectPage'), 'ObjectPage');
const RecordsPage = lazyRouteComponent(() => import('../pages/RecordsPage'), 'RecordsPage');
const ReportsPage = lazyRouteComponent(() => import('../pages/ReportsPage'), 'ReportsPage');
const ReportsTemplatesPage = lazyRouteComponent(
  () => import('../pages/ReportsToolsPage'),
  'ReportsTemplatesPage',
);
const ReportsMarksPage = lazyRouteComponent(() => import('../pages/ReportsToolsPage'), 'ReportsMarksPage');
const ReportBuilderPage = lazyRouteComponent(() => import('../pages/ReportBuilderPage'), 'ReportBuilderPage');
const StationReportPage = lazyRouteComponent(() => import('../pages/StationReportPage'), 'StationReportPage');
const AcquiringDocumentsPage = lazyRouteComponent(
  () => import('../pages/AcquiringDocumentsPage'),
  'AcquiringDocumentsPage',
);
const AlertsPage = lazyRouteComponent(() => import('../pages/AlertsPage'), 'AlertsPage');
const SettingsPage = lazyRouteComponent(() => import('../pages/SettingsPage'), 'SettingsPage');
const ProfilePage = lazyRouteComponent(() => import('../pages/ProfilePage'), 'ProfilePage');
const MorePage = lazyRouteComponent(() => import('../pages/MorePage'), 'MorePage');

const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFoundPage });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  pendingComponent: BootScreen,
  component: () => (
    <AuthOnly stage="login">
      <LoginPage />
    </AuthOnly>
  ),
});
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/setup',
  pendingComponent: BootScreen,
  component: () => (
    <AuthOnly stage="setup">
      <SetupPage />
    </AuthOnly>
  ),
});
const codeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/code',
  pendingComponent: BootScreen,
  component: () => (
    <AuthOnly stage="code">
      <CodePage />
    </AuthOnly>
  ),
});

const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppGate });

const summaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: () => <ScreenRoute section="summary" />,
});

const rentalsIndex = createRoute({
  getParentRoute: () => appRoute,
  path: '/rentals',
  beforeLoad: () => {
    throw redirect({ to: '/rentals/$tab', params: { tab: 'overview' } });
  },
});
const rentalsTab = createRoute({
  getParentRoute: () => appRoute,
  path: '/rentals/$tab',
  component: () => <ScreenRoute section="rentals" />,
});
const stationsIndex = createRoute({
  getParentRoute: () => appRoute,
  path: '/stations',
  beforeLoad: () => {
    throw redirect({ to: '/stations/$tab', params: { tab: 'overview' } });
  },
});
const stationsTab = createRoute({
  getParentRoute: () => appRoute,
  path: '/stations/$tab',
  beforeLoad: ({ params, search }) => {
    if (params.tab === 'map') throw redirect({ to: '/coverage', search: search as never });
  },
  component: () => <ScreenRoute section="stations" />,
});
const coverageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/coverage',
  component: () => <ScreenRoute section="coverage" />,
});
const clientsIndex = createRoute({
  getParentRoute: () => appRoute,
  path: '/clients',
  beforeLoad: () => {
    throw redirect({ to: '/clients/$tab', params: { tab: 'registrations' } });
  },
});
const clientsTab = createRoute({
  getParentRoute: () => appRoute,
  path: '/clients/$tab',
  component: () => <ScreenRoute section="clients" />,
});
const paymentsIndex = createRoute({
  getParentRoute: () => appRoute,
  path: '/payments',
  beforeLoad: () => {
    throw redirect({ to: '/payments/$tab', params: { tab: 'overview' } });
  },
});
const paymentsDocuments = createRoute({
  getParentRoute: () => appRoute,
  path: '/payments/documents',
  component: AcquiringDocumentsPage,
});
const paymentsTab = createRoute({
  getParentRoute: () => appRoute,
  path: '/payments/$tab',
  component: () => <ScreenRoute section="payments" />,
});

const objectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/objects/$type/$id',
  component: ObjectPage,
});

const recordsSearch = z.object({
  metric: z.string().max(120).catch(''),
  period: z.unknown().optional(),
  f: z.unknown().optional(),
  all: z.boolean().optional().catch(undefined),
  tz: z.enum(['msk', 'local']).optional().catch(undefined),
  tm: z.string().max(120).optional().catch(undefined),
});
const recordsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/records',
  validateSearch: recordsSearch,
  component: RecordsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  component: ReportsPage,
});
const reportNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/new',
  validateSearch: z.object({ r: reportDefinition.optional().catch(undefined) }),
  component: ReportBuilderPage,
});
const reportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/$id',
  component: ReportBuilderPage,
});
const stationReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/stations',
  component: StationReportPage,
});
const reportTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/templates',
  component: ReportsTemplatesPage,
});
const reportMarksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/marks',
  component: ReportsMarksPage,
});
const alertsRoute = createRoute({ getParentRoute: () => appRoute, path: '/alerts', component: AlertsPage });
const settingsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: () => <Navigate to="/settings/$tab" params={{ tab: 'users' }} replace />,
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/$tab',
  component: SettingsPage,
});
const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/profile',
  component: ProfilePage,
});
const moreRoute = createRoute({ getParentRoute: () => appRoute, path: '/more', component: MorePage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  setupRoute,
  codeRoute,
  appRoute.addChildren([
    summaryRoute,
    rentalsIndex,
    rentalsTab,
    stationsIndex,
    stationsTab,
    coverageRoute,
    clientsIndex,
    clientsTab,
    paymentsIndex,
    paymentsDocuments,
    paymentsTab,
    objectRoute,
    recordsRoute,
    reportsRoute,
    reportNewRoute,
    stationReportRoute,
    reportTemplatesRoute,
    reportMarksRoute,
    reportRoute,
    alertsRoute,
    settingsIndexRoute,
    settingsRoute,
    profileRoute,
    moreRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadDelay: 80,
  scrollRestoration: false,
  parseSearch,
  stringifySearch,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
